import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  FiSearch,
  FiTrash2,
  FiImage,
  FiArrowDown,
  FiArrowUp,
  FiFilter,
} from "react-icons/fi";
import { CATEGORIES, COLORS } from "../utils/constants";
import { formatCurrency, formatDate } from "../utils/helpers";
import { parseReceiptPaths, uploadsUrl } from "../api";
import ReceiptModal from "./ReceiptModal";

const Container = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray200};
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${COLORS.gray200};
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  padding: 20px 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Title = styled.h2`
  color: ${COLORS.gray800};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SearchWrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

const SearchIcon = styled(FiSearch)`
  color: ${COLORS.gray400};
  left: 10px;
  position: absolute;
`;

const SearchInput = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 13px;
  padding: 8px 10px 8px 32px;
  width: 180px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SearchWrapperFull = styled(SearchWrapper)`
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const FilterSelect = styled.select`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 13px;
  padding: 8px 10px;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }

  @media (max-width: 600px) {
    flex: 1;
    min-width: 0;
  }
`;

const Table = styled.div`
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  background: ${COLORS.gray50};
  border-bottom: 1px solid ${COLORS.gray200};
  color: ${COLORS.gray500};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 12px 16px;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td`
  border-bottom: 1px solid ${COLORS.gray100};
  color: ${COLORS.gray700};
  font-size: 14px;
  padding: 14px 16px;
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  background: ${(props) =>
    props.$type === "expense" ? COLORS.dangerLight : COLORS.successLight};
  border-radius: 12px;
  color: ${(props) =>
    props.$type === "expense" ? COLORS.danger : COLORS.success};
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
`;

const AmountCell = styled.span`
  color: ${(props) =>
    props.$type === "expense" ? COLORS.danger : COLORS.success};
  font-weight: 600;
`;

const ReceiptButton = styled.button`
  align-items: center;
  background: ${COLORS.primaryLight};
  border: none;
  border-radius: 4px;
  color: ${COLORS.primary};
  cursor: pointer;
  display: inline-flex;
  gap: 4px;
  font-size: 12px;
  padding: 4px 8px;

  &:hover {
    background: ${COLORS.primary};
    color: ${COLORS.white};
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.gray400};
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: ${COLORS.danger};
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: ${COLORS.gray400};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 48px 24px;
`;

const EmptyText = styled.p`
  font-size: 15px;
  margin: 0;
`;

const Description = styled.span`
  color: ${COLORS.gray500};
  font-size: 13px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

const MobileCards = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileCard = styled.div`
  border-bottom: 1px solid ${COLORS.gray100};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
`;

const MobileCardTop = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const MobileCardDate = styled.span`
  color: ${COLORS.gray500};
  font-size: 12px;
`;

const MobileCardMiddle = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
`;

const MobileCardDetail = styled.div`
  color: ${COLORS.gray500};
  font-size: 13px;
`;

const MobileCardActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const TransactionList = (props) => {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [receiptPreview, setReceiptPreview] = useState([]);

  const filtered = useMemo(() => {
    let result = [...props.transactions];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.payee?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.category?.toLowerCase().includes(q),
      );
    }

    if (categoryFilter) {
      result = result.filter((t) => t.category === categoryFilter);
    }

    if (typeFilter) {
      result = result.filter((t) => t.type === typeFilter);
    }

    result.sort((a, b) => new Date(b.date) - new Date(a.date));
    return result;
  }, [props.transactions, search, categoryFilter, typeFilter]);

  return (
    <React.Fragment>
      <Container>
        <Header>
          <Title>Transactions ({filtered.length})</Title>
          <Controls>
            <SearchWrapperFull>
              <SearchIcon size={14} />
              <SearchInput
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapperFull>
            <FilterSelect
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="expense">Expenses</option>
              <option value="replenish">Funds Added</option>
            </FilterSelect>
            <FilterSelect
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </FilterSelect>
          </Controls>
        </Header>

        {filtered.length === 0 ? (
          <EmptyState>
            <FiFilter size={32} />
            <EmptyText>
              {props.transactions.length === 0
                ? "No transactions yet. Record your first expense above."
                : "No transactions match your filters."}
            </EmptyText>
          </EmptyState>
        ) : (
          <React.Fragment>
            <Table>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                    <Th>Category</Th>
                    <Th>Payee</Th>
                    <Th>Description</Th>
                    <Th>Receipt</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id}>
                      <Td>{formatDate(t.date)}</Td>
                      <Td>
                        <TypeBadge $type={t.type}>
                          {t.type === "expense" ? (
                            <React.Fragment>
                              <FiArrowDown
                                size={12}
                                style={{
                                  verticalAlign: "middle",
                                  marginRight: 2,
                                }}
                              />
                              Expense
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <FiArrowUp
                                size={12}
                                style={{
                                  verticalAlign: "middle",
                                  marginRight: 2,
                                }}
                              />
                              Funded
                            </React.Fragment>
                          )}
                        </TypeBadge>
                      </Td>
                      <Td>
                        <AmountCell $type={t.type}>
                          {t.type === "expense" ? "-" : "+"}
                          {formatCurrency(t.amount, t.currency)}
                        </AmountCell>
                      </Td>
                      <Td>{t.category}</Td>
                      <Td>{t.payee || "--"}</Td>
                      <Td>
                        <Description title={t.description}>
                          {t.description || "--"}
                        </Description>
                      </Td>
                      <Td>
                        {parseReceiptPaths(t.receipt_path).length > 0 ? (
                          <ReceiptButton
                            onClick={() =>
                              setReceiptPreview(
                                parseReceiptPaths(t.receipt_path).map(
                                  uploadsUrl,
                                ),
                              )
                            }
                          >
                            <FiImage size={14} /> View
                            {parseReceiptPaths(t.receipt_path).length > 1 &&
                              ` (${parseReceiptPaths(t.receipt_path).length})`}
                          </ReceiptButton>
                        ) : (
                          "--"
                        )}
                      </Td>
                      <Td>
                        <DeleteButton
                          onClick={() => {
                            if (
                              window.confirm(
                                "Delete this transaction? This cannot be undone.",
                              )
                            ) {
                              props.onDeleteTransaction(t.id);
                            }
                          }}
                          title="Delete transaction"
                        >
                          <FiTrash2 size={16} />
                        </DeleteButton>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </Table>

            <MobileCards>
              {filtered.map((t) => (
                <MobileCard key={t.id}>
                  <MobileCardTop>
                    <TypeBadge $type={t.type}>
                      {t.type === "expense" ? (
                        <React.Fragment>
                          <FiArrowDown
                            size={12}
                            style={{
                              verticalAlign: "middle",
                              marginRight: 2,
                            }}
                          />
                          Expense
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <FiArrowUp
                            size={12}
                            style={{
                              verticalAlign: "middle",
                              marginRight: 2,
                            }}
                          />
                          Funded
                        </React.Fragment>
                      )}
                    </TypeBadge>
                    <AmountCell $type={t.type}>
                      {t.type === "expense" ? "-" : "+"}
                      {formatCurrency(t.amount, t.currency)}
                    </AmountCell>
                  </MobileCardTop>
                  <MobileCardMiddle>
                    <MobileCardDetail>
                      {t.category}
                      {t.payee ? ` -- ${t.payee}` : ""}
                    </MobileCardDetail>
                    <MobileCardDate>{formatDate(t.date)}</MobileCardDate>
                  </MobileCardMiddle>
                  {t.description && (
                    <MobileCardDetail>{t.description}</MobileCardDetail>
                  )}
                  <MobileCardActions>
                    {parseReceiptPaths(t.receipt_path).length > 0 && (
                      <ReceiptButton
                        onClick={() =>
                          setReceiptPreview(
                            parseReceiptPaths(t.receipt_path).map(uploadsUrl),
                          )
                        }
                      >
                        <FiImage size={14} /> View
                        {parseReceiptPaths(t.receipt_path).length > 1 &&
                          ` (${parseReceiptPaths(t.receipt_path).length})`}
                      </ReceiptButton>
                    )}
                    <DeleteButton
                      onClick={() => {
                        if (
                          window.confirm(
                            "Delete this transaction? This cannot be undone.",
                          )
                        ) {
                          props.onDeleteTransaction(t.id);
                        }
                      }}
                      title="Delete transaction"
                    >
                      <FiTrash2 size={16} />
                    </DeleteButton>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCards>
          </React.Fragment>
        )}
      </Container>

      <ReceiptModal
        receipts={receiptPreview}
        onClose={() => setReceiptPreview([])}
      />
    </React.Fragment>
  );
};

export default TransactionList;
